import React, { useEffect, useState } from "react";
import { Form, Input, Button, Typography, Modal, Spin } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import axios from "axios";
import { BACKEND_API_URL } from "../../environment";
import { useParams, useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const LandingPasswordChange = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [statusError, setStatusError] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [passwordUpdated, setPasswordUpdated] = useState(false);
    const [subscriptionType, setSubscriptionType] = useState("");

    const getRedirectUrl = () => {
        const forthApps = ["ForthERP", "ForthBooks", "HRPayroll"];
        const pharmaApps = ["PharmaERP", "PharmaBooks"];
        if (forthApps.includes(subscriptionType)) {
            return "https://erp.forthapps.com/";
        }
        if (pharmaApps.includes(subscriptionType)) {
            return "https://pharmaerp.forthapps.com/";
        }
        return "https://erp.forthapps.com/";
    };

    useEffect(() => {
        if (!passwordUpdated) return;

        const timer = setTimeout(() => {
            window.location.href = getRedirectUrl();
        }, 3000);

        return () => clearTimeout(timer);
    }, [passwordUpdated, subscriptionType]);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            setStatusError(true);
            setStatusMessage("No token provided.");
            return;
        }
        const validateToken = async () => {
            try {
                const res = await axios.post(`${BACKEND_API_URL}/verify-password-set`, { token });

                if (res.status === 200 && res.data?.data) {
                    form.setFieldsValue({ email: res.data.data.email });
                    setSubscriptionType(res.data.data.subscription_type);
                    setStatusError(false);
                    setStatusMessage("Token is valid. You can set your password.");
                } else {
                    setStatusError(true);
                    setStatusMessage("Invalid or expired token.");
                }
            } catch {
                setStatusError(true);
                setStatusMessage("Request a New Password Reset Link.");
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token, form]);



    const validatePassword = (p) =>
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/.test(p);

    const onFinish = async (values) => {
        if (!validatePassword(values.password))
            return Modal.warning({
                title:
                    "Password must be 8–16 characters and include uppercase, lowercase, number & special character.",
            });

        if (values.password !== values.confirmPassword)
            return Modal.warning({ title: "Passwords do not match." });

        try {
            const res = await axios.post(`${BACKEND_API_URL}/user-password-update-via-landing`, {
                ...values,
                tokenvalue: token,
            });

            if (res.data.error) return Modal.warning({ title: res.data.message });
            Modal.success({
                title: res.data.message || "Password updated successfully",
            });

            setPasswordUpdated(true);
            form.resetFields();

            // End any session already open in this browser.
            localStorage.removeItem("token");
            localStorage.removeItem("auth_info");
            sessionStorage.clear();

        } catch (err) {
            Modal.error({
                title: "Error updating password",
                content: err.response?.data?.message || "Please try again later.",
            });
        }
    };

    return (
        <div className="password-wrapper">
            {loading ? (
                <Spin tip="Validating token..." size="large" />
            ) : (
                <div className="password-card">
                    <div className="header">
                        <Title level={2} className="gradient-title">
                            Set Password
                        </Title>
                        {!passwordUpdated && (
                            <> <Text className="status-text" type={statusError ? "danger" : "secondary"}>{statusMessage}</Text></>
                        )}
                    </div>

                    {!statusError && !passwordUpdated && (
                        <Form form={form} layout="vertical" onFinish={onFinish}>
                            <Form.Item label="Email" name="email" hidden>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: "Password is required" }]}
                            >
                                <Input.Password
                                    placeholder="Enter new password"
                                    iconRender={(vis) => (vis ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            {/* <Text type="secondary" className="hint-text">
                                Password must be 8–16 characters. <br></br>Must include uppercase, lowercase, number & symbol.
                            </Text> */}

                            <Text type="secondary" className="hint-text">
                                Password must be 8–16 characters.
                                <br />
                                Include at least one uppercase letter, one lowercase letter,
                                <br />
                                one number, and one special character
                                <br />
                                <strong>@ $ ! % * ? &</strong>
                            </Text>

                            <Form.Item
                                name="confirmPassword"
                                dependencies={["password"]}
                                rules={[
                                    { required: true, message: "Confirm your password" },
                                    ({ getFieldValue }) => ({
                                        validator(_, v) {
                                            return !v || getFieldValue("password") === v
                                                ? Promise.resolve()
                                                : Promise.reject("Passwords do not match");
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    placeholder="Confirm new password"
                                    iconRender={(vis) => (vis ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" size="large" block className="submit-btn">
                                Save Password
                            </Button>
                        </Form>

                    )}

                    {passwordUpdated && (
                        <div className="success-message">
                            <h3 style={{ color: "green", fontWeight: 600 }}>🎉 Password Updated Successfully!</h3>

                            <p style={{ marginTop: "10px" }}>
                                Redirecting to login...
                            </p>

                        </div>
                    )}
                </div>
            )}

            {/* Styles */}
            <style>{`
        .password-wrapper {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
        }

        .password-card {
          width: clamp(280px, 85%, 420px);
          background: #fff;
          padding: clamp(20px, 4vw, 35px);
          border-radius: 16px;
          box-shadow: 0px 8px 30px rgba(0,0,0,0.1);
          animation: fadeIn .4s;
        }

        .header {
          text-align: center;
          margin-bottom: 15px;
        }

        .gradient-title {
          background: linear-gradient(90deg,#6366f1,#8b5cf6,#3b82f6);
          -webkit-background-clip: text;
          color: transparent;
          font-weight: 800;
          font-size: clamp(22px, 5vw, 32px);
        }

        .submit-btn {
          background: linear-gradient(90deg,#6366f1,#8b5cf6,#3b82f6);
          border: none;
          border-radius: 10px;
          font-weight: 600;
          marginTop:"5px"
        }

        


.hint-text {
  font-size: 12px;
  display: block;
  margin-bottom: 10px;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
}

.status-text {
  font-size: 14px;
  display: block;
  margin-top: 6px;
  line-height: 1.4;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 400px) {
  .status-text {
    font-size: 12px;
  }

  .hint-text {
    font-size: 10px;
  }
}

.success-message {
  text-align: center;
  padding: 20px;
  margin-top: 10px;
}

      `}</style>
        </div>
    );
};

export default LandingPasswordChange;
